package com.arena_web.core.viewmodel

import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.data.dto.*
import com.arena_web.data.http.isNetworkError
import com.arena_web.ui.localization.currentAppStrings
import com.arena_web.data.websocket.ChatWebSocketClient
import com.arena_web.data.websocket.ChatWsEvent
import com.arena_web.data.websocket.EventWebSocketClient
import com.arena_web.data.websocket.EventWsEvent
import com.arena_web.domain.repositories.ChatRepository
import com.arena_web.domain.repositories.EventRepository
import com.arena_web.domain.repositories.TeamRepository
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import com.arena_web.utils.MetaPixel
import com.arena_web.utils.TikTokPixel
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

enum class UserParticipation {
    NONE, PENDING, CONFIRMED, REJECTED
}

data class EventDetailUiState(
    val isLoading: Boolean = true,
    val event: EventDetailDto? = null,
    val isAdmin: Boolean = false,
    val userParticipation: UserParticipation = UserParticipation.NONE,
    val actionInProgress: Boolean = false,
    val error: String? = null,
    val teamSearchResults: List<TeamDto> = emptyList(),
    val invitations: List<TeamInvitationDto> = emptyList(),
    val inviteInProgress: Boolean = false,
    val teamSearchLoading: Boolean = false,
    // Match (IN_PROGRESS) state
    val timerSeconds: Long = 0,
    val timerRunning: Boolean = false,
    val timerStatus: String = "IDLE", // IDLE, RUNNING, PAUSED, STOPPED
    // COMPLETED state
    val playerStats: EventPlayerStatsResultDto? = null,
    val ratings: List<EventPlayerRatingDto> = emptyList(),
    // True while the Rate Performance popup's Submit POST is in flight.
    // Drives the popup's spinner + disables the submit button until the
    // backend response (success or failure) comes back.
    val ratingSubmitInProgress: Boolean = false,
    val mediaItems: List<EventMediaDto> = emptyList(),
    val mediaLoading: Boolean = false,
    // In-flight media uploads — the media grid renders these as loader
    // tiles at the top of the list until each POST resolves.
    val pendingMediaUploads: List<PendingMediaUpload> = emptyList(),
    // Uploads that didn't make it (too large / backend error) — the UI
    // surfaces them once the batch finishes, then clears via
    // [EventDetailViewModel.clearFailedMediaUploads].
    val failedMediaUploads: List<FailedMediaUpload> = emptyList(),
    // Event chat state
    val chatMessages: List<MessageDto> = emptyList(),
    val chatMessageText: String = "",
    val chatLoading: Boolean = false,
    val chatPage: Int = 0,
    val chatHasMore: Boolean = true,
    val chatReplyingTo: MessageDto? = null,
    val chatEditing: MessageDto? = null,
    val chatPendingFile: PendingFile? = null,
    val chatUploadingFile: Boolean = false,
    val chatPinnedMessage: MessageDto? = null,
    val chatTypingUsers: Map<String, String> = emptyMap(),
)

// Client-side gate mirroring the Add Media popup's "max 30 MB" copy (the
// backend itself allows 50 MB).
private const val MAX_MEDIA_UPLOAD_BYTES = 30.0 * 1024 * 1024

/** A media file handed to [EventDetailViewModel.uploadMediaBatch] — the raw
 *  browser [org.w3c.files.File] plus the object URL the picker created for
 *  its thumbnail (the batch reuses it for the loader tile and revokes it). */
data class MediaUploadRequest(
    val file: org.w3c.files.File,
    val previewUrl: String,
)

/** One in-flight upload, rendered as a loader tile in the media grid. */
data class PendingMediaUpload(
    val localId: Int,
    val fileName: String,
    val previewUrl: String,
    val isVideo: Boolean,
)

/** One upload that failed — file name + human-readable reason. Keeps the
 *  local preview object URL alive so the failure report can show the media;
 *  [EventDetailViewModel.clearFailedMediaUploads] revokes it. */
data class FailedMediaUpload(
    val fileName: String,
    val reason: String,
    val previewUrl: String? = null,
    val isVideo: Boolean = false,
)

class EventDetailViewModel(
    private val eventId: String,
    private val eventRepository: EventRepository? = null,
    private val publicEventRepository: EventRepository,
    private val teamRepository: TeamRepository? = null,
    private val chatRepository: ChatRepository? = null,
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(EventDetailUiState())
    val uiState: StateFlow<EventDetailUiState> = _uiState.asStateFlow()

    private val chatWsClient = ChatWebSocketClient()
    private val eventWsClient = EventWebSocketClient()
    private val typingClearJobs = mutableMapOf<String, Job>()

    // Track the chat we're currently subscribed to and the collector job.
    // loadEvent() used to call connectChatWebSocket() on every refresh which
    // tore down + reopened the socket each time (and leaked collectors).
    private var connectedChatId: String? = null
    private var chatCollectorJob: Job? = null

    private var _onEventDeleted: (() -> Unit)? = null

    fun onEventDeletedRemotely(callback: () -> Unit) {
        _onEventDeleted = callback
    }

    override fun onCreate() {
        super.onCreate()
        loadEvent()
        connectEventWebSocket()
    }

    override fun onDispose() {
        chatWsClient.disconnect()
        eventWsClient.disconnect()
        typingClearJobs.values.forEach { it.cancel() }
        typingClearJobs.clear()
        super.onDispose()
    }

    fun loadEvent() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.event == null, error = null) }

            val repo = eventRepository ?: publicEventRepository
            val result = repo.getEvent(eventId)

            result.onSuccess { event ->
                val currentUserId = Storage.get(StorageKeys.USER_ID)
                val participant = event.participants.find { it.userId == currentUserId }
                // Admin rights flow strictly through participation now — backend
                // also enforces `participation && isAdmin` (see requireAdmin in
                // EventUseCaseUtils), so if the creator leaves they lose
                // operational control until they rejoin and get re-promoted.
                val isAdmin = participant?.isAdmin == true

                val participation = when (participant?.status?.uppercase()) {
                    "CONFIRMED" -> UserParticipation.CONFIRMED
                    "PENDING" -> UserParticipation.PENDING
                    "REJECTED" -> UserParticipation.REJECTED
                    else -> UserParticipation.NONE
                }

                _uiState.update {
                    it.copy(
                        event = event,
                        isAdmin = isAdmin,
                        userParticipation = participation,
                        isLoading = false,
                        actionInProgress = false,
                    )
                }

                // Load invitations if admin
                if (isAdmin) loadInvitations()

                // Load timer + live player stats if event is in progress.
                // Player stats also drive the four corner StatBlocks on the
                // match-view player cards, so they need to be pre-loaded
                // before the field renders.
                if (event.status.uppercase() == "IN_PROGRESS") {
                    loadTimer()
                    loadPlayerStats()
                }

                // Load stats and media if event is completed
                if (event.status.uppercase() == "COMPLETED") {
                    loadPlayerStats()
                    loadMedia()
                    loadRatings()
                }

                // Load event chat if available and user is a participant
                if (event.chatId != null && participation == UserParticipation.CONFIRMED && chatRepository != null) {
                    loadChatMessages()
                    connectChatWebSocket()
                }
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, isLoading = false, actionInProgress = false) }
            }
        }
    }

    fun joinEvent() {
        val repo = eventRepository ?: return
        AnalyticsTracker.trackClick("join_event", screen = "event_details", params = mapOf("event_id" to eventId))
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.joinEvent(eventId).onSuccess { participant ->
                MetaPixel.trackJoinEvent(eventId)
                TikTokPixel.trackJoinEvent(eventId)
                applyParticipantPatch(participant, isSelfAction = true)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun leaveEvent() {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.leaveEvent(eventId).onSuccess {
                val selfId = Storage.get(StorageKeys.USER_ID)
                applyParticipantRemoval(selfId, isSelfAction = true)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun confirmParticipant(userId: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.confirmParticipant(eventId, userId).onSuccess { participant ->
                applyParticipantPatch(participant)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun rejectParticipant(userId: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.rejectParticipant(eventId, userId).onSuccess {
                applyParticipantRemoval(userId)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun createEventTeam(name: String, color: String?) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.addTeam(eventId, AddEventTeamRequest(name = name, color = color)).onSuccess { team ->
                applyTeamAdded(team)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun assignTeam(eventTeamId: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.assignTeam(eventId, AssignTeamRequest(eventTeamId)).onSuccess { participant ->
                applyParticipantPatch(participant)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun assignPlayerToTeam(userId: String, eventTeamId: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.assignUserToTeam(eventId, userId, AssignTeamRequest(eventTeamId)).onSuccess { participant ->
                applyParticipantPatch(participant)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun leaveTeam() {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.leaveTeam(eventId).onSuccess { participant ->
                applyParticipantPatch(participant)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun toggleParticipantAdmin(userId: String, makeAdmin: Boolean) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.setParticipantAdmin(eventId, userId, makeAdmin).onSuccess { participant ->
                applyParticipantPatch(participant)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun removePlayerFromTeam(userId: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.removeUserFromTeam(eventId, userId).onSuccess { participant ->
                applyParticipantPatch(participant)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun deleteTeam(teamId: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.deleteTeam(eventId, teamId).onSuccess {
                applyTeamRemoved(teamId)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun startEvent() {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            // Only Pro events have the structured live match view (formation,
            // stat tracking, lineup swaps). Non-Pro and "Other" sport events
            // are casual / informal sessions, so "start" jumps straight to
            // COMPLETED and organisers wrap up with a single click.
            val targetStatus = if (_uiState.value.event?.isPro == true) {
                "IN_PROGRESS"
            } else {
                "COMPLETED"
            }
            repo.updateEventStatus(eventId, UpdateEventStatusRequest(targetStatus)).onSuccess { updated ->
                applyStatusTransition(updated)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    fun editEvent(request: UpdateEventRequest, onSuccess: () -> Unit = {}, onError: (String) -> Unit = {}) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.updateEvent(eventId, request).onSuccess { updated ->
                _uiState.update { it.copy(event = updated, actionInProgress = false) }
                onSuccess()
            }.onFailure { e ->
                val msg = e.message ?: "Failed to update event"
                _uiState.update { it.copy(error = msg, actionInProgress = false) }
                onError(msg)
            }
        }
    }

    /** Apply an externally-fetched updated event (e.g. from CreateEventPopup in edit mode). */
    fun applyEventUpdate(updated: EventDetailDto) {
        _uiState.update { it.copy(event = updated) }
    }

    // ── Granular state mutations ──
    // Action endpoints now return only the affected entity. These helpers
    // merge the patch into the cached event so we never refetch the whole
    // payload on a confirm / assign / admin-toggle / etc.

    private fun applyParticipantPatch(patch: EventParticipantDto, isSelfAction: Boolean = false) {
        _uiState.update { state ->
            val event = state.event ?: return@update state.copy(actionInProgress = false)
            val existingIndex = event.participants.indexOfFirst { it.userId == patch.userId }
            val newParticipants =
                if (existingIndex >= 0) {
                    event.participants.toMutableList().also { it[existingIndex] = patch }
                } else {
                    event.participants + patch
                }
            val isSelf = isSelfAction || patch.userId == Storage.get(StorageKeys.USER_ID)
            val newParticipation =
                if (isSelf) {
                    when (patch.status.uppercase()) {
                        "CONFIRMED" -> UserParticipation.CONFIRMED
                        "PENDING" -> UserParticipation.PENDING
                        "REJECTED" -> UserParticipation.REJECTED
                        else -> state.userParticipation
                    }
                } else state.userParticipation
            // Recompute the top-level admin flag from the patch — same rule
            // as loadEvent() (participant.isAdmin only; creatorId no longer
            // grants implicit rights).
            val newIsAdmin = if (isSelf) patch.isAdmin else state.isAdmin
            state.copy(
                event = event.copy(participants = newParticipants),
                userParticipation = newParticipation,
                isAdmin = newIsAdmin,
                actionInProgress = false,
            )
        }
    }

    private fun applyParticipantRemoval(userId: String?, isSelfAction: Boolean = false) {
        if (userId == null) {
            _uiState.update { it.copy(actionInProgress = false) }
            return
        }
        _uiState.update { state ->
            val event = state.event ?: return@update state.copy(actionInProgress = false)
            val filtered = event.participants.filterNot { it.userId == userId }
            val isSelf = isSelfAction || userId == Storage.get(StorageKeys.USER_ID)
            val newParticipation = if (isSelf) UserParticipation.NONE else state.userParticipation
            // Self left the event — drop admin rights too. Creator stays the
            // creator on the backend but loses operational control (Edit /
            // Start / Delete / ⋮-menu) until they rejoin and are re-promoted.
            val newIsAdmin = if (isSelf) false else state.isAdmin
            state.copy(
                event = event.copy(participants = filtered),
                userParticipation = newParticipation,
                isAdmin = newIsAdmin,
                actionInProgress = false,
            )
        }
    }

    private fun applyTeamAdded(team: EventTeamDto) {
        _uiState.update { state ->
            val event = state.event ?: return@update state.copy(actionInProgress = false)
            // Backend guarantees a new id, but guard against echoes from
            // WS-triggered re-applies just in case.
            if (event.teams.any { it.id == team.id }) {
                return@update state.copy(actionInProgress = false)
            }
            state.copy(event = event.copy(teams = event.teams + team), actionInProgress = false)
        }
    }

    private fun applyTeamRemoved(teamId: String) {
        _uiState.update { state ->
            val event = state.event ?: return@update state.copy(actionInProgress = false)
            val newTeams = event.teams.filterNot { it.id == teamId }
            // Drop the team assignment off any participant that was on the
            // removed team — backend does the same in the DB so this keeps the
            // local cache consistent without a full refetch.
            val newParticipants = event.participants.map {
                if (it.eventTeamId == teamId) it.copy(eventTeamId = null) else it
            }
            state.copy(
                event = event.copy(teams = newTeams, participants = newParticipants),
                actionInProgress = false,
            )
        }
    }

    // ── WS-triggered variants (don't touch actionInProgress) ──
    // Action-handler helpers above reset actionInProgress; that's wrong for
    // updates pushed by another client mid-action, so the WS path uses these
    // siblings that mutate state.event only.

    private fun applyParticipantPatchFromWs(patch: EventParticipantDto) {
        val isSelf = patch.userId == Storage.get(StorageKeys.USER_ID)
        val wasConfirmed = _uiState.value.userParticipation == UserParticipation.CONFIRMED
        _uiState.update { state ->
            val event = state.event ?: return@update state
            val existingIndex = event.participants.indexOfFirst { it.userId == patch.userId }
            val newParticipants =
                if (existingIndex >= 0) {
                    event.participants.toMutableList().also { it[existingIndex] = patch }
                } else {
                    event.participants + patch
                }
            val newParticipation =
                if (isSelf) {
                    when (patch.status.uppercase()) {
                        "CONFIRMED" -> UserParticipation.CONFIRMED
                        "PENDING" -> UserParticipation.PENDING
                        "REJECTED" -> UserParticipation.REJECTED
                        else -> state.userParticipation
                    }
                } else state.userParticipation
            // Recompute admin flag from the patch — strictly participation-based
            // now, so a WS push of `isAdmin=false` strips Edit/Start/Delete even
            // for the event creator.
            val newIsAdmin = if (isSelf) patch.isAdmin else state.isAdmin
            state.copy(
                event = event.copy(participants = newParticipants),
                userParticipation = newParticipation,
                isAdmin = newIsAdmin,
            )
        }
        // Self just transitioned from non-CONFIRMED to CONFIRMED — admin
        // approved them from another tab/device. Open the event chat we
        // previously skipped because participation didn't qualify.
        if (isSelf && !wasConfirmed &&
            _uiState.value.userParticipation == UserParticipation.CONFIRMED &&
            chatRepository != null && _uiState.value.event?.chatId != null
        ) {
            loadChatMessages()
            connectChatWebSocket()
        }
    }

    private fun applyParticipantRemovalFromWs(userId: String) {
        _uiState.update { state ->
            val event = state.event ?: return@update state
            val filtered = event.participants.filterNot { it.userId == userId }
            val isSelf = userId == Storage.get(StorageKeys.USER_ID)
            val newParticipation = if (isSelf) UserParticipation.NONE else state.userParticipation
            // Cross-tab parity with applyParticipantRemoval: if I get removed
            // (kicked / I leave from another device) my admin flag drops too.
            val newIsAdmin = if (isSelf) false else state.isAdmin
            state.copy(
                event = event.copy(participants = filtered),
                userParticipation = newParticipation,
                isAdmin = newIsAdmin,
            )
        }
    }

    private fun applyTeamAddedFromWs(team: EventTeamDto) {
        _uiState.update { state ->
            val event = state.event ?: return@update state
            if (event.teams.any { it.id == team.id }) return@update state
            state.copy(event = event.copy(teams = event.teams + team))
        }
    }

    private fun applyTeamRemovedFromWs(teamId: String) {
        _uiState.update { state ->
            val event = state.event ?: return@update state
            val newTeams = event.teams.filterNot { it.id == teamId }
            val newParticipants = event.participants.map {
                if (it.eventTeamId == teamId) it.copy(eventTeamId = null) else it
            }
            state.copy(event = event.copy(teams = newTeams, participants = newParticipants))
        }
    }

    fun deleteEvent(onSuccess: () -> Unit = {}, onError: (String) -> Unit = {}) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.deleteEvent(eventId).onSuccess {
                _uiState.update { it.copy(actionInProgress = false) }
                onSuccess()
            }.onFailure { e ->
                val msg = e.message ?: "Failed to delete event"
                _uiState.update { it.copy(error = msg, actionInProgress = false) }
                onError(msg)
            }
        }
    }

    fun searchTeams(query: String) {
        val repo = teamRepository ?: return
        val sportId = _uiState.value.event?.sportId
        viewModelScope.launch {
            _uiState.update { it.copy(teamSearchLoading = true) }
            repo.searchTeams(query, sportId).onSuccess { teams ->
                _uiState.update { it.copy(teamSearchResults = teams, teamSearchLoading = false) }
            }.onFailure {
                _uiState.update { it.copy(teamSearchResults = emptyList(), teamSearchLoading = false) }
            }
        }
    }

    fun inviteTeam(teamId: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(inviteInProgress = true) }
            repo.createTeamInvitation(eventId, CreateTeamInvitationRequest(teamId)).onSuccess {
                loadInvitations()
                _uiState.update { it.copy(inviteInProgress = false) }
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, inviteInProgress = false) }
            }
        }
    }

    fun loadInvitations() {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            repo.getTeamInvitations(eventId).onSuccess { invitations ->
                _uiState.update { it.copy(invitations = invitations) }
            }
        }
    }

    fun clearTeamSearch() {
        _uiState.update { it.copy(teamSearchResults = emptyList()) }
    }

    // ── COMPLETED methods ──

    fun loadPlayerStats() {
        val repo = eventRepository ?: publicEventRepository
        viewModelScope.launch {
            repo.getStats(eventId).onSuccess { stats ->
                _uiState.update { it.copy(playerStats = stats) }
            }
        }
    }

    /** Per-event, per-reviewer ratings. Drives the popup's pre-fill
     *  (so a re-vote starts on the user's previous stars) and the
     *  Lineups card ★ overlay (average across all reviewers). */
    fun loadRatings() {
        val repo = eventRepository ?: publicEventRepository
        viewModelScope.launch {
            repo.getRatings(eventId).onSuccess { ratings ->
                _uiState.update { it.copy(ratings = ratings) }
            }
        }
    }

    /**
     * Submit a rating for [userId] from the Rate Performance popup. Builds
     * the `RatePlayerRequest` (general = average of per-attribute scores,
     * details = each attribute's star count as a BigDecimal-compatible
     * string) and POSTs it via the authenticated event repository.
     * Requires the user to be logged-in — `eventRepository` is null for
     * anonymous viewers, in which case the call no-ops.
     */
    fun ratePlayer(userId: String, attributeRatings: Map<String, Int>) {
        val repo = eventRepository ?: return
        // Backend enforces `value ∈ [1.0, 5.0]` for every detailed rating
        // (see `RatePlayerUseCase` — 0-valued entries trip a 400 with
        // "All detailed rating values must be between 1.0 and 5.0").
        // Filter out unrated attributes before building the request.
        val scored = attributeRatings.filterValues { it in 1..5 }
        if (scored.isEmpty()) return
        val avg = scored.values.sum().toDouble() / scored.size
        val general = ((avg * 10).toInt() / 10.0).toString()
        val details = scored.map { (attrId, value) ->
            DetailedRatingRequest(attributeId = attrId, value = value.toString())
        }
        _uiState.update { it.copy(ratingSubmitInProgress = true) }
        viewModelScope.launch {
            val outcome = repo.ratePlayer(
                eventId,
                userId,
                RatePlayerRequest(generalRating = general, detailedRatings = details),
            )
            outcome.onFailure {
                // Always release the flag so the popup spinner clears.
                _uiState.update { it.copy(ratingSubmitInProgress = false) }
            }
            outcome.onSuccess { saved ->
                // Backend now ships both aggregates in the POST response
                // (`eventAverageRating` + `playerOverallRating`), so we
                // skip the previous `loadRatings()` + `loadEvent()`
                // follow-ups and patch the local state in one pass.
                _uiState.update { state ->
                    // Replace this reviewer's previous rating for this
                    // player (if any), otherwise append the new one.
                    val existing = state.ratings.indexOfFirst {
                        it.reviewerId == saved.reviewerId &&
                            it.ratedUserId == saved.ratedUserId
                    }
                    val updatedRatings = if (existing >= 0) {
                        state.ratings.toMutableList().also { it[existing] = saved }
                    } else {
                        state.ratings + saved
                    }
                    // Patch the rated participant's `overallRating` so
                    // the FIFA OVR badge updates in place. Backend sends
                    // the 1.0–5.0 weighted figure; ×20 lands in the
                    // 0–100 scale the frontend renders via `.toInt()`.
                    val ovr1to5 = saved.playerOverallRating?.toDoubleOrNull() ?: 0.0
                    val ovrFifa = ovr1to5 * 20.0
                    val updatedEvent = state.event?.let { ev ->
                        ev.copy(
                            participants = ev.participants.map { p ->
                                if (p.userId == userId) p.copy(overallRating = ovrFifa)
                                else p
                            },
                        )
                    }
                    state.copy(
                        ratings = updatedRatings,
                        event = updatedEvent,
                        ratingSubmitInProgress = false,
                    )
                }
            }
        }
    }

    /**
     * Swap two participants' `lineup_order` so the live-match formation
     * arrangement persists. Backend updates the rows and broadcasts
     * `EVENT_UPDATED` carrying the new participant order, so all viewers
     * converge on the same lineup.
     */
    fun swapLineup(userIdA: String, userIdB: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            repo.swapLineup(eventId, SwapLineupRequest(userIdA = userIdA, userIdB = userIdB))
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message) }
                }
        }
    }

    /**
     * Swap two `event_teams` display orders so the live-match view cycles
     * which two teams appear in the left/right field slots. Backend
     * broadcasts `EVENT_UPDATED` so all viewers converge.
     */
    fun swapEventTeams(teamIdA: String, teamIdB: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            repo.swapEventTeams(eventId, SwapEventTeamsRequest(teamIdA = teamIdA, teamIdB = teamIdB))
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message) }
                }
        }
    }

    /**
     * Push a single stat counter update for a player. Backend persists the
     * value and broadcasts `STAT_UPDATE` over WebSocket; the WS handler then
     * triggers a refetch so every viewer (including this client) converges
     * on the canonical value.
     */
    fun setPlayerStat(
        targetUserId: String,
        statTypeId: String,
        value: Int,
    ) {
        val repo = eventRepository ?: return
        // This client is the writer — its optimistic state IS the truth.
        // Ignore the echo of our own write coming back as a STAT_UPDATE
        // broadcast: refetching would race rapid taps and bounce the counter
        // back to a stale value. The window renews on every tap.
        suppressStatRefetchUntilMs = kotlin.js.Date().getTime() + 3000
        viewModelScope.launch {
            // Optimistic local patch — replace the matching entry (or add
            // it if missing) so the tapping admin sees the counter move
            // immediately, before the broadcast round-trip lands.
            _uiState.update { state ->
                val existing = state.playerStats ?: return@update state
                val others = existing.playerStats.filterNot {
                    it.userId == targetUserId && it.statTypeId == statTypeId
                }
                val patched =
                    others + EventPlayerStatDto(
                        id = "optimistic-$targetUserId-$statTypeId",
                        eventId = eventId,
                        userId = targetUserId,
                        statTypeId = statTypeId,
                        value = value,
                    )
                state.copy(playerStats = existing.copy(playerStats = patched))
            }
            repo.setPlayerStats(
                eventId = eventId,
                userId = targetUserId,
                request = SetPlayerStatsRequest(
                    stats = listOf(PlayerStatEntry(statTypeId = statTypeId, value = value)),
                ),
            ).onFailure { e ->
                _uiState.update { it.copy(error = e.message) }
                // Roll back the optimistic patch by refetching server truth
                // (and stop suppressing broadcasts — our write didn't land).
                suppressStatRefetchUntilMs = 0.0
                loadPlayerStats()
            }
        }
    }

    /** Epoch ms until which `STAT_UPDATE` broadcasts are echoes of this
     *  client's own writes and must not trigger a stats refetch. */
    private var suppressStatRefetchUntilMs = 0.0

    fun loadMedia() {
        val repo = eventRepository ?: publicEventRepository
        viewModelScope.launch {
            _uiState.update { it.copy(mediaLoading = true) }
            repo.getMedia(eventId).onSuccess { media ->
                _uiState.update { it.copy(mediaItems = media, mediaLoading = false) }
            }.onFailure {
                _uiState.update { it.copy(mediaLoading = false) }
            }
        }
    }

    fun deleteMedia(mediaId: String) {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            repo.deleteMedia(eventId, mediaId).onSuccess {
                loadMedia()
            }
        }
    }

    /**
     * Upload a single media file. The WebSocket `MEDIA_UPLOADED` broadcast
     * (handled in [handleWebSocketEvent]) appends the new item to the grid,
     * so we don't manually refresh the list here.
     *
     * Returns the result so callers (e.g. AddMediaPopup) can show per-file
     * status / partial-success errors when uploading several files at once.
     */
    suspend fun uploadMedia(
        fileBytes: ByteArray,
        fileName: String,
        contentType: String,
    ): Result<com.arena_web.data.dto.EventMediaDto> {
        val repo = eventRepository ?: return Result.failure(IllegalStateException("Not authenticated"))
        return repo.uploadMedia(eventId, fileBytes, fileName, contentType)
    }

    /**
     * Fire-and-forget batch upload — the Add Media sheet closes immediately
     * and each file shows as a loader tile ([PendingMediaUpload]) at the top
     * of the media grid while it uploads. Per file: size gate → read bytes →
     * client-side image compression → POST; success prepends the returned
     * item (newest-first, the WS echo is deduped), failure lands in
     * [EventDetailUiState.failedMediaUploads] once the whole batch finishes
     * so the UI can list what didn't make it.
     */
    fun uploadMediaBatch(requests: List<MediaUploadRequest>) {
        val repo = eventRepository ?: return
        if (requests.isEmpty()) return
        val entries = requests.map { req ->
            PendingMediaUpload(
                localId = ++mediaUploadLocalId,
                fileName = req.file.name,
                previewUrl = req.previewUrl,
                isVideo = req.file.type.startsWith("video/"),
            )
        }
        _uiState.update { it.copy(pendingMediaUploads = it.pendingMediaUploads + entries) }
        viewModelScope.launch {
            val failed = mutableListOf<FailedMediaUpload>()
            requests.zip(entries).forEach { (req, entry) ->
                fun failure(reason: String) = FailedMediaUpload(
                    fileName = req.file.name,
                    reason = reason,
                    previewUrl = req.previewUrl,
                    isVideo = req.file.type.startsWith("video/"),
                )
                var fail: FailedMediaUpload? = null
                try {
                    if (req.file.size.toDouble() > MAX_MEDIA_UPLOAD_BYTES) {
                        fail = failure("File is larger than 30 MB")
                    } else {
                        val bytes = readFileBytes(req.file)
                        val (uploadBytes, contentType) =
                            com.arena_web.utils.compressImageForUpload(bytes, req.file.type)
                        repo.uploadMedia(eventId, uploadBytes, req.file.name, contentType)
                            .onSuccess { media ->
                                _uiState.update { s ->
                                    if (s.mediaItems.any { it.id == media.id }) s
                                    else s.copy(mediaItems = listOf(media) + s.mediaItems)
                                }
                            }
                            // Backend/proxy rejections of big payloads come
                            // back as an opaque "Internal server error" —
                            // useless to the user, so the report speaks in
                            // terms of the size limit — except transport
                            // failures, where "check your connection" is the
                            // actionable truth.
                            .onFailure { e ->
                                fail = failure(
                                    if (e.isNetworkError()) currentAppStrings().commonErrorConnection
                                    else "File is larger than 30 MB"
                                )
                            }
                    }
                } catch (e: Throwable) {
                    fail = failure("File is larger than 30 MB")
                } finally {
                    _uiState.update { s ->
                        s.copy(pendingMediaUploads = s.pendingMediaUploads.filterNot { it.localId == entry.localId })
                    }
                    // Failed entries keep their object URL alive for the
                    // failure report; clearFailedMediaUploads revokes them.
                    val f = fail
                    if (f == null) {
                        org.w3c.dom.url.URL.revokeObjectURL(req.previewUrl)
                    } else {
                        failed += f
                    }
                }
            }
            if (failed.isNotEmpty()) {
                _uiState.update { it.copy(failedMediaUploads = it.failedMediaUploads + failed) }
            }
        }
    }

    /** Dismiss one entry from the failure report (the ✕ on its preview). */
    fun removeFailedMediaUpload(fail: FailedMediaUpload) {
        fail.previewUrl?.let { org.w3c.dom.url.URL.revokeObjectURL(it) }
        _uiState.update { s ->
            val idx = s.failedMediaUploads.indexOf(fail)
            if (idx < 0) s
            else s.copy(
                failedMediaUploads = s.failedMediaUploads.toMutableList().also { it.removeAt(idx) },
            )
        }
    }

    fun clearFailedMediaUploads() {
        _uiState.value.failedMediaUploads.forEach { f ->
            f.previewUrl?.let { org.w3c.dom.url.URL.revokeObjectURL(it) }
        }
        _uiState.update { it.copy(failedMediaUploads = emptyList()) }
    }

    private var mediaUploadLocalId = 0

    private suspend fun readFileBytes(file: org.w3c.files.File): ByteArray =
        kotlinx.coroutines.suspendCancellableCoroutine { cont ->
            val reader = org.w3c.files.FileReader()
            reader.onload = {
                val buffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                cont.resume(org.khronos.webgl.Int8Array(buffer).unsafeCast<ByteArray>()) { _, _, _ -> }
                null
            }
            reader.onerror = {
                cont.cancel(RuntimeException("Failed to read \"${file.name}\""))
                null
            }
            reader.readAsArrayBuffer(file)
        }

    // ── Match (IN_PROGRESS) methods ──

    fun endMatch() {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(actionInProgress = true) }
            repo.updateEventStatus(eventId, UpdateEventStatusRequest("COMPLETED")).onSuccess { updated ->
                applyStatusTransition(updated)
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message, actionInProgress = false) }
            }
        }
    }

    private fun applyStatusTransition(updated: EventDetailDto) {
        // Merge the freshly-cleaned event (e.g. unassigned participants
        // dropped on NEW → IN_PROGRESS) into state, then kick off the
        // status-specific loads that were previously chained to loadEvent.
        _uiState.update { state ->
            val current = state.event
            state.copy(
                event = if (current != null) updated.copy(timer = current.timer) else updated,
                actionInProgress = false,
            )
        }
        val s = updated.status.uppercase()
        if (s == "IN_PROGRESS") loadTimer()
        if (s == "COMPLETED") {
            loadPlayerStats()
            loadMedia()
        }
    }

    fun updateScore(teamId: String, delta: Int) {
        val repo = eventRepository ?: return
        val currentTeam = _uiState.value.event?.teams?.find { it.id == teamId } ?: return
        val newScore = (currentTeam.score + delta).coerceAtLeast(0)
        // Optimistic patch — the scoreboard moves on the tap itself, and rapid
        // taps each read the previous tap's fresh value instead of the stale
        // pre-request score. Same self-echo rule as `setPlayerStat`: we wrote
        // the value, so the SCORE_UPDATE broadcast coming back (and a refetch)
        // would only race newer taps and bounce the counter.
        _uiState.update { state ->
            val ev = state.event ?: return@update state
            state.copy(
                event = ev.copy(
                    teams = ev.teams.map {
                        if (it.id == teamId) it.copy(score = newScore) else it
                    },
                ),
            )
        }
        suppressScoreEchoUntilMs = kotlin.js.Date().getTime() + 3000
        // One in-flight request per team, always carrying the LATEST desired
        // score. Firing a POST per tap would race on the wire — the requests
        // hold absolute values, so whichever landed last would win on the
        // backend, not the newest one. The sender loop below drains
        // `desiredTeamScores` until the acked value matches the latest tap.
        desiredTeamScores[teamId] = newScore
        if (scoreSenders[teamId]?.isActive != true) {
            scoreSenders[teamId] = viewModelScope.launch {
                while (true) {
                    val target = desiredTeamScores[teamId] ?: break
                    val res = repo.setTeamScore(eventId, teamId, SetTeamScoreRequest(target))
                    if (res.isFailure) {
                        _uiState.update { it.copy(error = res.exceptionOrNull()?.message) }
                        // Roll back the optimistic patch by refetching server truth.
                        suppressScoreEchoUntilMs = 0.0
                        desiredTeamScores.remove(teamId)
                        loadEvent()
                        break
                    }
                    if (desiredTeamScores[teamId] == target) {
                        desiredTeamScores.remove(teamId)
                        break
                    }
                }
            }
        }
    }

    /** Epoch ms until which `SCORE_UPDATE` broadcasts are echoes of this
     *  client's own writes and must not overwrite the optimistic score. */
    private var suppressScoreEchoUntilMs = 0.0

    /** Latest score each team should converge on (per rapid-tap bursts). */
    private val desiredTeamScores = mutableMapOf<String, Int>()

    /** Per-team sender coroutine draining [desiredTeamScores]. */
    private val scoreSenders = mutableMapOf<String, kotlinx.coroutines.Job>()

    fun loadTimer() {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            repo.getTimer(eventId).onSuccess { timer ->
                val running = timer.status.uppercase() == "RUNNING"
                var elapsed = timer.elapsedSeconds
                if (running && timer.lastStartedAt != null) {
                    val startMs = kotlin.js.Date(timer.lastStartedAt).getTime()
                    val nowMs = kotlin.js.Date().getTime()
                    elapsed += ((nowMs - startMs) / 1000).toLong()
                }
                _uiState.update { it.copy(timerSeconds = elapsed, timerRunning = running, timerStatus = timer.status.uppercase()) }
            }
        }
    }

    fun toggleTimer() {
        val repo = eventRepository ?: return
        val status = _uiState.value.timerStatus
        val action = when {
            status == "RUNNING" -> "PAUSE"
            status == "PAUSED" -> "RESUME"
            else -> "START" // IDLE or STOPPED
        }
        viewModelScope.launch {
            repo.updateTimer(eventId, EventTimerActionRequest(action)).onSuccess { timer ->
                val running = timer.status.uppercase() == "RUNNING"
                _uiState.update { it.copy(timerSeconds = timer.elapsedSeconds, timerRunning = running, timerStatus = timer.status.uppercase()) }
            }
        }
    }

    fun resetTimer() {
        val repo = eventRepository ?: return
        viewModelScope.launch {
            repo.updateTimer(eventId, EventTimerActionRequest("RESET")).onSuccess { timer ->
                _uiState.update { it.copy(timerSeconds = 0, timerRunning = false, timerStatus = "IDLE") }
            }
        }
    }

    fun tickTimer() {
        if (_uiState.value.timerRunning) {
            _uiState.update { it.copy(timerSeconds = it.timerSeconds + 1) }
        }
    }

    fun toggleExtendedStats() {
        val repo = eventRepository ?: return
        val current = _uiState.value.event?.extendedStatsEnabled ?: false
        viewModelScope.launch {
            repo.toggleExtendedStats(eventId, ToggleExtendedStatsRequest(!current)).onSuccess {
                loadEvent()
            }.onFailure { e ->
                _uiState.update { it.copy(error = e.message) }
            }
        }
    }

    // ── Event Chat methods ──

    fun loadChatMessages() {
        val repo = chatRepository ?: return
        val chatId = _uiState.value.event?.chatId ?: return
        viewModelScope.launch {
            _uiState.update { it.copy(chatLoading = true) }
            repo.getMessages(chatId, mapOf("page" to "0", "size" to "50")).onSuccess { page ->
                _uiState.update {
                    it.copy(
                        chatMessages = page.messages.reversed(),
                        chatLoading = false,
                        chatPage = 0,
                        chatHasMore = page.messages.size >= 50,
                    )
                }
            }.onFailure {
                _uiState.update { it.copy(chatLoading = false) }
            }
        }
        loadPinnedChatMessage()
    }

    private fun loadPinnedChatMessage() {
        val repo = chatRepository ?: return
        val chatId = _uiState.value.event?.chatId ?: return
        viewModelScope.launch {
            repo.getPinnedMessages(chatId)
                .onSuccess { pinned ->
                    _uiState.update { it.copy(chatPinnedMessage = pinned.firstOrNull()) }
                }
                .onFailure {
                    _uiState.update { it.copy(chatPinnedMessage = null) }
                }
        }
    }

    fun loadMoreChatMessages() {
        val repo = chatRepository ?: return
        val chatId = _uiState.value.event?.chatId ?: return
        if (_uiState.value.chatLoading || !_uiState.value.chatHasMore) return
        val nextPage = _uiState.value.chatPage + 1
        viewModelScope.launch {
            _uiState.update { it.copy(chatLoading = true) }
            repo.getMessages(chatId, mapOf("page" to nextPage.toString(), "size" to "50")).onSuccess { page ->
                _uiState.update {
                    it.copy(
                        chatMessages = page.messages.reversed() + it.chatMessages,
                        chatLoading = false,
                        chatPage = nextPage,
                        chatHasMore = page.messages.size >= 50,
                    )
                }
            }.onFailure {
                _uiState.update { it.copy(chatLoading = false) }
            }
        }
    }

    fun sendChatMessage() {
        val repo = chatRepository ?: return
        val state = _uiState.value
        val chatId = state.event?.chatId ?: return
        val text = state.chatMessageText.trim()
        if (text.isEmpty()) return
        val replyToId = state.chatReplyingTo?.id
        _uiState.update { it.copy(chatMessageText = "", chatReplyingTo = null) }
        viewModelScope.launch {
            repo.sendMessage(chatId, SendMessageRequest(content = text, replyToMessageId = replyToId))
                .onSuccess { message ->
                    _uiState.update { s -> s.copy(chatMessages = s.chatMessages + message) }
                }
        }
    }

    fun updateChatMessageText(text: String) {
        _uiState.update { it.copy(chatMessageText = text) }
    }

    fun toggleChatReaction(messageId: String, emoji: String) {
        val repo = chatRepository ?: return
        val chatId = _uiState.value.event?.chatId ?: return
        val currentUserId = Storage.get(StorageKeys.USER_ID) ?: return
        val message = _uiState.value.chatMessages.find { it.id == messageId } ?: return
        val existing = message.reactions.find { it.emoji == emoji }
        val alreadyReacted = existing?.userIds?.contains(currentUserId) == true
        viewModelScope.launch {
            if (alreadyReacted) {
                repo.removeReaction(chatId, messageId, emoji)
            } else {
                repo.addReaction(chatId, messageId, AddReactionRequest(emoji = emoji))
            }
        }
    }

    fun startChatReply(message: MessageDto) {
        _uiState.update { it.copy(chatReplyingTo = message, chatEditing = null) }
    }

    fun cancelChatReply() {
        _uiState.update { it.copy(chatReplyingTo = null) }
    }

    fun startChatEdit(message: MessageDto) {
        _uiState.update {
            it.copy(chatEditing = message, chatMessageText = message.content, chatReplyingTo = null)
        }
    }

    fun cancelChatEdit() {
        _uiState.update { it.copy(chatEditing = null, chatMessageText = "") }
    }

    fun submitChatEdit() {
        val repo = chatRepository ?: return
        val state = _uiState.value
        val chatId = state.event?.chatId ?: return
        val editing = state.chatEditing ?: return
        val newContent = state.chatMessageText.trim()
        if (newContent.isEmpty() || newContent == editing.content) {
            cancelChatEdit()
            return
        }
        viewModelScope.launch {
            repo.editMessage(chatId, editing.id, EditMessageRequest(content = newContent))
                .onSuccess { updated ->
                    _uiState.update { s ->
                        s.copy(
                            chatMessages = s.chatMessages.map { if (it.id == updated.id) updated else it },
                            chatEditing = null,
                            chatMessageText = "",
                        )
                    }
                }
        }
    }

    fun deleteChatMessage(messageId: String) {
        val repo = chatRepository ?: return
        val chatId = _uiState.value.event?.chatId ?: return
        viewModelScope.launch {
            repo.deleteMessage(chatId, messageId).onSuccess {
                _uiState.update { state ->
                    state.copy(chatMessages = state.chatMessages.filter { it.id != messageId })
                }
            }
        }
    }

    fun pinChatMessage(messageId: String) {
        val repo = chatRepository ?: return
        val chatId = _uiState.value.event?.chatId ?: return
        viewModelScope.launch {
            repo.togglePin(chatId, messageId).onSuccess { updated ->
                _uiState.update { state ->
                    state.copy(
                        chatMessages = state.chatMessages.map { if (it.id == updated.id) updated else it },
                        chatPinnedMessage = if (updated.isPinned) updated else null,
                    )
                }
            }
        }
    }

    fun setChatPendingFile(bytes: ByteArray, name: String, contentType: String) {
        _uiState.update { it.copy(chatPendingFile = PendingFile(bytes, name, contentType)) }
    }

    fun clearChatPendingFile() {
        _uiState.update { it.copy(chatPendingFile = null) }
    }

    fun sendChatMediaMessage() {
        val repo = chatRepository ?: return
        val state = _uiState.value
        val chatId = state.event?.chatId ?: return
        val file = state.chatPendingFile ?: return
        _uiState.update { it.copy(chatUploadingFile = true) }
        viewModelScope.launch {
            repo.sendMediaMessage(chatId, file.bytes, file.name, file.contentType)
                .onSuccess { message ->
                    _uiState.update { s ->
                        s.copy(
                            chatMessages = s.chatMessages + message,
                            chatPendingFile = null,
                            chatUploadingFile = false,
                        )
                    }
                }
                .onFailure { e ->
                    _uiState.update { it.copy(error = e.message, chatUploadingFile = false) }
                }
        }
    }

    fun connectChatWebSocket() {
        val chatId = _uiState.value.event?.chatId ?: return
        // Idempotent: skip if we're already subscribed to this chat.
        // loadEvent() can fire repeatedly (initial load + edit-event echoes),
        // and each call used to reopen the socket and spawn a fresh collector,
        // burning bandwidth and leaving zombie collectors in scope.
        if (connectedChatId == chatId && chatCollectorJob?.isActive == true) return
        chatCollectorJob?.cancel()
        chatWsClient.connect(chatId)
        connectedChatId = chatId
        chatCollectorJob = viewModelScope.launch {
            chatWsClient.events.collect { event -> handleChatWsEvent(event) }
        }
    }

    private fun handleChatWsEvent(event: ChatWsEvent) {
        when (event) {
            is ChatWsEvent.NewMessage -> {
                val currentUserId = Storage.get(StorageKeys.USER_ID)
                if (event.message.senderId != currentUserId) {
                    _uiState.update { it.copy(chatMessages = it.chatMessages + event.message) }
                }
            }
            is ChatWsEvent.MessageDeleted -> {
                _uiState.update { state ->
                    state.copy(chatMessages = state.chatMessages.filter { it.id != event.data.messageId })
                }
            }
            is ChatWsEvent.MessageEdited -> {
                _uiState.update { state ->
                    state.copy(chatMessages = state.chatMessages.map { msg ->
                        if (msg.id == event.data.messageId) msg.copy(content = event.data.content, isEdited = true) else msg
                    })
                }
            }
            is ChatWsEvent.ReactionAdded -> {
                _uiState.update { state ->
                    state.copy(chatMessages = state.chatMessages.map { msg ->
                        if (msg.id == event.data.messageId) {
                            val existing = msg.reactions.find { it.emoji == event.data.emoji }
                            val newReactions = if (existing != null) {
                                msg.reactions.map { r ->
                                    if (r.emoji == event.data.emoji && !r.userIds.contains(event.data.userId)) {
                                        r.copy(count = r.count + 1, userIds = r.userIds + event.data.userId)
                                    } else r
                                }
                            } else {
                                msg.reactions + ReactionDto(
                                    emoji = event.data.emoji,
                                    count = 1,
                                    userIds = listOf(event.data.userId),
                                )
                            }
                            msg.copy(reactions = newReactions)
                        } else msg
                    })
                }
            }
            is ChatWsEvent.ReactionRemoved -> {
                _uiState.update { state ->
                    state.copy(chatMessages = state.chatMessages.map { msg ->
                        if (msg.id == event.data.messageId) {
                            val newReactions = msg.reactions.mapNotNull { r ->
                                if (r.emoji == event.data.emoji) {
                                    val newUserIds = r.userIds - event.data.userId
                                    if (newUserIds.isEmpty()) null
                                    else r.copy(count = newUserIds.size, userIds = newUserIds)
                                } else r
                            }
                            msg.copy(reactions = newReactions)
                        } else msg
                    })
                }
            }
            is ChatWsEvent.UserTyping -> {
                val userId = event.data.userId
                val username = event.data.username ?: userId
                _uiState.update { it.copy(chatTypingUsers = it.chatTypingUsers + (userId to username)) }
                typingClearJobs[userId]?.cancel()
                typingClearJobs[userId] = viewModelScope.launch {
                    delay(3000)
                    _uiState.update { it.copy(chatTypingUsers = it.chatTypingUsers - userId) }
                }
            }
            else -> { /* MemberJoined/Left/Kicked, Online/Offline, Connected/Disconnected — ignore */ }
        }
    }

    fun sendChatTyping() {
        chatWsClient.sendTyping()
    }

    // ── Event real-time updates ──

    private fun connectEventWebSocket() {
        eventWsClient.connect(eventId)
        viewModelScope.launch {
            eventWsClient.events.collect { event -> handleEventWsEvent(event) }
        }
    }

    @Suppress("CyclomaticComplexMethod", "LongMethod")
    private fun handleEventWsEvent(wsEvent: EventWsEvent) {
        when (wsEvent) {
            is EventWsEvent.ScoreUpdate -> {
                // Skip our own echo — during rapid tapping the broadcast of an
                // older tap can arrive after a newer optimistic value and would
                // bounce the scoreboard backwards. Other clients still apply it.
                if (kotlin.js.Date().getTime() < suppressScoreEchoUntilMs) return
                _uiState.update { state ->
                    val ev = state.event ?: return@update state
                    state.copy(
                        event = ev.copy(
                            teams = ev.teams.map {
                                if (it.id == wsEvent.eventTeamId) it.copy(score = wsEvent.score) else it
                            },
                        ),
                    )
                }
            }
            is EventWsEvent.TimerUpdate -> {
                val running = wsEvent.timer.status.uppercase() == "RUNNING"
                var elapsed = wsEvent.timer.elapsedSeconds
                if (running && wsEvent.timer.lastStartedAt != null) {
                    val startMs = kotlin.js.Date(wsEvent.timer.lastStartedAt).getTime()
                    val nowMs = kotlin.js.Date().getTime()
                    elapsed += ((nowMs - startMs) / 1000).toLong()
                }
                _uiState.update {
                    it.copy(
                        timerSeconds = elapsed,
                        timerRunning = running,
                        timerStatus = wsEvent.timer.status.uppercase(),
                    )
                }
            }
            is EventWsEvent.StatUpdate -> {
                // Refetch stats whenever the live match view (IN_PROGRESS)
                // or the wrap-up (COMPLETED) is open, so every viewer's UI
                // converges on the latest counter values broadcast by the
                // backend after a `setPlayerStats` call — UNLESS this client
                // wrote a stat moments ago: then the broadcast is (almost
                // certainly) our own echo and the optimistic local value is
                // already correct; refetching would bounce the counter.
                val statusUpper = _uiState.value.event?.status?.uppercase()
                val ownEcho = kotlin.js.Date().getTime() < suppressStatRefetchUntilMs
                if (!ownEcho && (statusUpper == "COMPLETED" || statusUpper == "IN_PROGRESS")) {
                    loadPlayerStats()
                }
            }
            is EventWsEvent.RatingReceived -> {
                if (_uiState.value.event?.status?.uppercase() == "COMPLETED") {
                    loadPlayerStats()
                    loadRatings()
                    // Re-pull participants so other viewers see the
                    // weighted FIFA OVR refresh after someone else voted.
                    loadEvent()
                }
            }
            is EventWsEvent.MediaUploaded -> {
                // Newest-first + dedupe: our own uploads already prepended
                // via uploadMediaBatch, so the echo must not double them.
                _uiState.update { state ->
                    if (state.mediaItems.any { it.id == wsEvent.media.id }) state
                    else state.copy(mediaItems = listOf(wsEvent.media) + state.mediaItems)
                }
            }
            is EventWsEvent.MediaDeleted -> {
                _uiState.update { state ->
                    state.copy(mediaItems = state.mediaItems.filter { it.id != wsEvent.mediaId })
                }
            }
            is EventWsEvent.Substitution -> loadEvent()
            // Apply the broadcast event directly — no GET. Preserve fields
            // that aren't part of the WS payload (timer, extendedStats) by
            // merging instead of replacing wholesale.
            is EventWsEvent.EventUpdated -> _uiState.update { state ->
                val current = state.event
                state.copy(event = if (current != null) wsEvent.event.copy(
                    timer = current.timer,
                ) else wsEvent.event)
            }
            is EventWsEvent.EventDeleted -> _onEventDeleted?.invoke()
            is EventWsEvent.StatusChanged -> {
                _uiState.update { state ->
                    val current = state.event
                    state.copy(event = if (current != null) wsEvent.event.copy(
                        timer = current.timer,
                    ) else wsEvent.event)
                }
                val s = wsEvent.event.status.uppercase()
                if (s == "IN_PROGRESS") loadTimer()
                if (s == "COMPLETED") {
                    loadPlayerStats()
                    loadMedia()
                }
            }
            // Participant/team events now carry the affected DTO in the WS
            // payload, so other clients (and other tabs) get the same merge
            // the originator applied from the action response — no full event
            // refetch needed.
            is EventWsEvent.ParticipantJoined -> applyParticipantPatchFromWs(wsEvent.participant)
            is EventWsEvent.ParticipantLeft -> applyParticipantRemovalFromWs(wsEvent.userId)
            is EventWsEvent.ParticipantConfirmed -> applyParticipantPatchFromWs(wsEvent.participant)
            is EventWsEvent.ParticipantRejected -> applyParticipantRemovalFromWs(wsEvent.userId)
            is EventWsEvent.ParticipantAdminChanged -> applyParticipantPatchFromWs(wsEvent.participant)
            is EventWsEvent.TeamAssigned -> applyParticipantPatchFromWs(wsEvent.participant)
            is EventWsEvent.TeamLeft -> applyParticipantPatchFromWs(wsEvent.participant)
            is EventWsEvent.TeamAdded -> applyTeamAddedFromWs(wsEvent.team)
            is EventWsEvent.TeamRemoved -> applyTeamRemovedFromWs(wsEvent.eventTeamId)
            is EventWsEvent.ExtendedStatsToggled -> {
                _uiState.update { state ->
                    val ev = state.event ?: return@update state
                    state.copy(event = ev.copy(extendedStatsEnabled = wsEvent.enabled))
                }
            }
            EventWsEvent.Connected, EventWsEvent.Disconnected -> { /* no-op */ }
        }
    }
}
